import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useGetDeviceRenewalRequestsQuery } from "../../redux/admin/deviceApi";
import ScrollableTableContainer from "../../components/common/ScrollableTableContainer";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN");
};

export default function RenewalRequest() {
  const [search, setSearch] = useState("");
  const [page] = useState(1);
  const [limit] = useState(50);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetDeviceRenewalRequestsQuery({ page, limit });

  const rows = useMemo(() => {
    const list = Array.isArray(data?.data) ? data.data : [];
    const q = String(search || "").trim().toLowerCase();
    if (!q) return list;

    return list.filter((row) => {
      const haystack = [
        row?.name,
        row?.vehicleName,
        row?.imei,
        row?.registrationNo,
        row?.requestedByName,
        row?.requestedByRole,
        row?.billingBarcodeOwnerName,
        row?.remarks,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [data?.data, search]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ p: 2 }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Renewal Request
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payment proof requests visible in your account scope.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search vehicle / customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon fontSize="small" color="action" />,
              }}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {error?.data?.msg || error?.data?.message || "Failed to load renewal requests"}
            </Alert>
          </Box>
        ) : (
          <ScrollableTableContainer maxHeight={540}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Barcode Owner</TableCell>
                  <TableCell>Requested On</TableCell>
                  <TableCell>Plan / Amount</TableCell>
                  <TableCell>Payment Proof</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography fontWeight={700}>No renewal requests found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>
                        <Typography fontWeight={800}>{row?.vehicleName || row?.name || row?.registrationNo || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row?.registrationNo || row?.imei || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>{row?.requestedByName || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row?.requestedByRole || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>{row?.billingBarcodeOwnerName || "-"}</TableCell>
                      <TableCell>{formatDateTime(row?.requestedAt)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>{row?.rechargePlanName || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ₹{Number(row?.perVehicleAmount || 0).toLocaleString("en-IN")} / vehicle
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row?.paymentScreenshot ? (
                          <Box
                            component="a"
                            href={row.paymentScreenshot}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ display: "inline-flex" }}
                          >
                            <Box
                              component="img"
                              src={row.paymentScreenshot}
                              alt="Payment proof"
                              sx={{
                                width: 56,
                                height: 56,
                                objectFit: "cover",
                                borderRadius: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{row?.remarks || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        )}

        {isFetching && !isLoading ? <Box sx={{ px: 2, py: 1 }}><CircularProgress size={18} /></Box> : null}
      </Paper>
    </Box>
  );
}
